import { useState, useEffect, useMemo } from "react";
import { api, useAuth } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Snowflake,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
  MapPin,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { es as esLocale, fr as frLocale } from "date-fns/locale";
import NumericInput from "@/components/NumericInput";

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function Refrigerators() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dateLocale = lang === "fr" ? frLocale : esLocale;

  const [refrigerators, setRefrigerators] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingFridge, setEditingFridge] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    min_temp: "0",
    max_temp: "4",
  });

  // Load refrigerators + logs in parallel — reuse single fetch, no duplicated calls.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError(false);
      try {
        const [fRes, lRes] = await Promise.all([
          api.get("/refrigerators"),
          api.get("/temperature-logs"),
        ]);
        if (cancelled) return;
        setRefrigerators(fRes.data || []);
        setLogs(lRes.data || []);
      } catch (err) {
        if (cancelled) return;
        console.error("[Refrigerators] load failed:", err);
        setLoadError(true);
        toast.error(t("fridge.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [t]);

  // Group logs per refrigerator (sorted DESC by logged_at). Derived cache.
  const logsByFridge = useMemo(() => {
    const map = new Map();
    for (const log of logs) {
      if (!map.has(log.refrigerator_id)) map.set(log.refrigerator_id, []);
      map.get(log.refrigerator_id).push(log);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(b.logged_at) - new Date(a.logged_at));
    }
    return map;
  }, [logs]);

  // ─── CRUD handlers ───
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!formData.name || !formData.location) {
      toast.error(t("addProduct.requiredFieldsError") || "Faltan campos");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        min_temp: parseFloat(formData.min_temp),
        max_temp: parseFloat(formData.max_temp),
      };
      if (editingFridge) {
        const response = await api.put(
          `/refrigerators/${editingFridge.refrigerator_id}`,
          payload
        );
        setRefrigerators((prev) =>
          prev.map((f) =>
            f.refrigerator_id === editingFridge.refrigerator_id ? response.data : f
          )
        );
      } else {
        const response = await api.post("/refrigerators", payload);
        setRefrigerators((prev) => [...prev, response.data]);
      }
      closeModal();
    } catch (error) {
      console.error("[Refrigerators] save failed:", error);
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isAdmin) return;
    if (!deleteId) return;
    try {
      await api.delete(`/refrigerators/${deleteId}`);
      setRefrigerators((prev) => prev.filter((f) => f.refrigerator_id !== deleteId));
    } catch (error) {
      console.error("[Refrigerators] delete failed:", error);
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const openEditModal = (fridge) => {
    if (!isAdmin) return;
    setEditingFridge(fridge);
    setFormData({
      name: fridge.name,
      location: fridge.location,
      min_temp: fridge.min_temp.toString(),
      max_temp: fridge.max_temp.toString(),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFridge(null);
    setFormData({ name: "", location: "", min_temp: "0", max_temp: "4" });
  };

  // ─── Render ───
  return (
    <div data-testid="refrigerators-page" className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground tracking-tight">
            {t("fridge.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t("fridge.subtitle")}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowModal(true)} data-testid="add-refrigerator-btn" size="sm">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t("fridge.addFridge")}
          </Button>
        )}
      </div>

      {/* States */}
      {loading ? (
        <SkeletonGrid />
      ) : loadError ? (
        <Card>
          <CardContent className="p-6 flex flex-col items-center gap-2 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <p className="text-sm">{t("fridge.loadError")}</p>
          </CardContent>
        </Card>
      ) : refrigerators.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Snowflake className="h-12 w-12 mb-4 opacity-40" />
            <p>{t("fridge.noFridges")}</p>
            {isAdmin && (
              <Button
                variant="outline"
                className="mt-4 border-primary text-primary hover:bg-primary/5"
                onClick={() => setShowModal(true)}
                data-testid="empty-add-refrigerator-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("fridge.addFirst")}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {refrigerators.map((fridge) => (
            <FridgeCard
              key={fridge.refrigerator_id}
              fridge={fridge}
              logs={logsByFridge.get(fridge.refrigerator_id) || []}
              dateLocale={dateLocale}
              onEdit={openEditModal}
              onDelete={setDeleteId}
              isAdmin={isAdmin}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Add / Edit modal — behaviour preserved */}
      <Dialog open={isAdmin && showModal} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingFridge ? t("fridge.editTitle") : t("fridge.addTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("fridge.name")}</Label>
              <Input
                id="name"
                placeholder={t("fridge.namePlaceholder")}
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
                data-testid="fridge-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">{t("fridge.location")}</Label>
              <Input
                id="location"
                placeholder={t("fridge.locationPlaceholder")}
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                required
                data-testid="fridge-location-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_temp">{t("fridge.minTempLabel")}</Label>
                <NumericInput
                  id="min_temp"
                  value={formData.min_temp}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, min_temp: e.target.value }))
                  }
                  required
                  data-testid="min-temp-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_temp">{t("fridge.maxTempLabel")}</Label>
                <NumericInput
                  id="max_temp"
                  value={formData.max_temp}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, max_temp: e.target.value }))
                  }
                  required
                  data-testid="max-temp-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-fridge-btn">
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {submitting
                  ? t("common.saving")
                  : editingFridge
                  ? t("fridge.update")
                  : t("fridge.add")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isAdmin && !!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fridge.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("fridge.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─────────────────────────────────────────────
// Fridge card — the monitoring tile
// ─────────────────────────────────────────────
function FridgeCard({ fridge, logs, dateLocale, onEdit, onDelete, isAdmin, t }) {
  const latest = logs[0] || null;
  const historyPoints = logs.slice(0, 5).slice().reverse(); // oldest → newest for sparkline

  // Status
  const status = !latest ? "none" : latest.is_within_range ? "ok" : "oor";
  const statusCfg = {
    ok:   { label: t("fridge.statusNormal"),   Icon: CheckCircle2,  dot: "bg-emerald-500", chip: "border-primary/30 bg-primary/5 text-primary" },
    oor:  { label: t("fridge.statusOOR"),      Icon: AlertTriangle, dot: "bg-red-500",     chip: "border-destructive/40 bg-red-50 text-destructive" },
    none: { label: t("fridge.statusNoData"),   Icon: MinusCircle,   dot: "bg-zinc-400",    chip: "border-zinc-300 bg-zinc-50 text-zinc-600" },
  }[status];

  // Last reading relative time
  let lastReadingText = null;
  if (latest?.logged_at) {
    try {
      lastReadingText = formatDistanceToNow(parseISO(latest.logged_at), {
        addSuffix: true,
        locale: dateLocale,
      });
    } catch {
      try { lastReadingText = format(parseISO(latest.logged_at), "d MMM yyyy · HH:mm", { locale: dateLocale }); }
      catch { lastReadingText = latest.logged_at; }
    }
  }

  // Range position (0..1) — clamp so OOR values sit at extremes
  const rangeSpan = fridge.max_temp - fridge.min_temp;
  const rawPos = latest && rangeSpan > 0
    ? (latest.temperature - fridge.min_temp) / rangeSpan
    : null;
  const clampedPos = rawPos == null ? null : Math.min(1, Math.max(0, rawPos));

  return (
    <Card
      className={`overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
        status === "oor" ? "border-red-200" : "border-border"
      }`}
      data-testid={`fridge-card-${fridge.refrigerator_id}`}
    >
      <CardContent className="p-0">
        {/* Top strip: name / actions */}
        <div className="flex items-start justify-between gap-2 p-4 pb-2">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                status === "oor" ? "bg-red-50" : "bg-primary/10"
              }`}
            >
              <Snowflake
                className={`h-5 w-5 ${status === "oor" ? "text-destructive" : "text-primary"}`}
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-semibold text-base truncate leading-tight">
                {fridge.name}
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                <MapPin className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{fridge.location}</span>
              </p>
            </div>
          </div>
          {isAdmin && <div className="flex gap-0.5 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(fridge)}
              data-testid={`edit-${fridge.refrigerator_id}`}
              aria-label="Editar"
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-red-50 hover:text-destructive"
              onClick={() => onDelete(fridge.refrigerator_id)}
              data-testid={`delete-${fridge.refrigerator_id}`}
              aria-label="Eliminar"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>}
        </div>

        {/* Current reading + status badge */}
        <div className="px-4 pb-3 pt-1 flex items-baseline justify-between gap-2">
          <div>
            <p className={`text-3xl font-bold font-mono leading-tight tracking-tight ${
              status === "oor" ? "text-destructive" : status === "ok" ? "text-foreground" : "text-muted-foreground"
            }`}>
              {latest ? `${latest.temperature}°C` : "—"}
            </p>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
              <Clock className="h-3 w-3" aria-hidden="true" />
              {lastReadingText || t("fridge.statusNoData")}
            </p>
          </div>
          <Badge
            variant="outline"
            className={`gap-1.5 font-medium text-[11px] py-0.5 px-2 border transition-colors ${statusCfg.chip}`}
            data-testid={`status-badge-${fridge.refrigerator_id}`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} aria-hidden="true" />
            {statusCfg.label}
          </Badge>
        </div>

        {/* Range bar */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>{fridge.min_temp}°C</span>
            <span className="uppercase tracking-wide text-[10px] font-medium">
              {t("fridge.rangeLabel")}
            </span>
            <span>{fridge.max_temp}°C</span>
          </div>
          <div className="relative h-2 rounded-full bg-zinc-100 overflow-hidden">
            {/* subtle full-width in-range zone */}
            <div className="absolute inset-y-0 left-0 right-0 bg-primary/10" />
            {clampedPos != null && (
              <>
                {/* fill from left to current position */}
                <div
                  className={`absolute inset-y-0 left-0 transition-all duration-500 ${
                    status === "oor" ? "bg-destructive/70" : "bg-primary"
                  }`}
                  style={{ width: `${clampedPos * 100}%` }}
                />
                {/* handle */}
                <div
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm transition-all duration-500 ${
                    status === "oor" ? "bg-destructive" : "bg-primary"
                  }`}
                  style={{ left: `${clampedPos * 100}%` }}
                />
              </>
            )}
          </div>
        </div>

        {/* Sparkline / empty history */}
        <div className="px-4 pb-4 pt-1 border-t border-border/40">
          {historyPoints.length >= 2 ? (
            <Sparkline
              points={historyPoints}
              min={fridge.min_temp}
              max={fridge.max_temp}
            />
          ) : historyPoints.length === 1 ? (
            <p className="text-[11px] text-muted-foreground text-center py-3">
              {latest?.temperature}°C
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground text-center py-3 italic">
              {t("fridge.noHistory")}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Mini SVG sparkline (last 5 readings)
// ─────────────────────────────────────────────
function Sparkline({ points, min, max }) {
  const W = 240;
  const H = 34;
  const P = 3;

  const values = points.map((p) => Number(p.temperature));
  const lo = Math.min(min, ...values);
  const hi = Math.max(max, ...values);
  const range = hi - lo || 1;

  const step = points.length > 1 ? (W - P * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = P + i * step;
    const y = P + (1 - (Number(p.temperature) - lo) / range) * (H - P * 2);
    return { x, y, oor: !p.is_within_range, temperature: p.temperature };
  });
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const hasOOR = coords.some((c) => c.oor);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-9"
      role="img"
      aria-label="Últimos registros"
    >
      <path
        d={path}
        fill="none"
        stroke={hasOOR ? "#D9381E" : "#1B4D3E"}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <circle
          key={`sp-${i}-${c.x.toFixed(1)}`}
          cx={c.x}
          cy={c.y}
          r={2}
          fill={c.oor ? "#D9381E" : "#1B4D3E"}
        />
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────
// Skeleton grid while loading
// ─────────────────────────────────────────────
function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fridge-skeleton">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`sk-${i}`}
          className="rounded-lg border bg-white p-4 space-y-3 animate-pulse"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-zinc-200 rounded w-2/3" />
              <div className="h-3 bg-zinc-100 rounded w-1/2" />
            </div>
          </div>
          <div className="h-9 bg-zinc-100 rounded" />
          <div className="h-2 bg-zinc-100 rounded" />
          <div className="h-9 bg-zinc-100 rounded" />
        </div>
      ))}
    </div>
  );
}
